// 引入必要的工具函数和事件总线
import { formatWebplImg, formatGifFrame, formatThumbnailImg } from '_lib/utils/utilsImg';
import { setImgSrc } from '@/ChatUI/utils/img-lazy-load';

const PRELOAD_IMG = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==';

class ChatImg {
  constructor(props = {}) {
    // 默认属性
    this.props = Object.assign({
      src: '', // 图片链接
      lazy: true, // 是否开启懒加载
      animation: true, // 是否展示懒加载动画
      webp_quality: 100, // 转换 webp 质量
      
      fit: 'cover', // 图片渲染方式，同 object-fit
      img_info: {}, // 图像信息，存高宽
      thumbnail: '', // 缩略图参数
      backend_gif_pause: true, // 后端 GIF 暂停
      gif_active_type: 'focus', // 激活 GIF 的类型 ['hover', 'all', 'focus']
      hover_dom_handler: null, // 自定义 hover 元素获取函数
      error: true, // 是否允许显示错误
      classList: [], // 自定义 class
      attrs: '', // 自定义属性
      imgAttrs: '', // img 标签自定义属性
      isActive: false, // 是否选中
    }, props);
    if (this.props.src) {
      if (typeof this.props.src === 'object') {
        this.props.src = this.props.src.src_url;
      }
      this.props.src = this.props.src.replace(/&#46;/g, '.');
    }

    // 数据属性
    this.showViewer = false;
    this.hidden_gif = false;
    this.is_gif_active = false;
    this._listeners = [];
    this.ele = null;

  }
  get is_gif() {
    return /\.gif/i.test(this.props.src);
  }

  get real_src() {
    let url = this.props.src;
    if (this.props.webp_quality > 0 && this.props.webp_quality < 100) {
      url = formatWebplImg(url, this.props.webp_quality);
    }
    if (this.is_gif && this.props.gif_active_type !== 'all' && !this.is_gif_active) {
      url = formatGifFrame(url);
    }
    if (this.props.thumbnail) {
      url = formatThumbnailImg(url, this.props.thumbnail);
    }
    return url;
  }
  calculateDivSize(imgWidth, imgHeight, maxWidth = 500, maxHeight = 500) {
    // 如果图片的宽度和高度都小于等于最大值，则不需要缩放
    if (imgWidth <= maxWidth && imgHeight <= maxHeight) {
      return { width: imgWidth, height: imgHeight };
    }
  
    // 计算缩放因子
    const scaleFactor = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
  
    // 计算 div 的实际宽高
    const width = parseInt(imgWidth * scaleFactor);
    const height = parseInt(imgHeight * scaleFactor);
  
    return { width, height };
  }
  // 生成最终的 HTML 字符串
  render() {
    // 外层 div 的 class
    const divClassList = ['cpt-chat-img'];
    if (this.props.img_info && this.props.img_info.width) {
      divClassList.push('img-info-block');
    }
    if (this.props.classList && this.props.classList.length > 0) {
      divClassList.push(...this.props.classList);
    }
    let divClass = divClassList.join(' ');
    
    // 内部 img 元素
    let preloadImgHtml = '';
    let divStyle = '';    
    if (this.props.img_info && this.props.img_info.width) {
      const { width, height } = this.calculateDivSize(this.props.img_info.width, this.props.img_info.height);
      divStyle = `max-width: ${width}px; max-height: ${height}px;`;
      const preloadSrc = `data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect opacity="0" width="${width}" height="${height}"/></svg>`;
      preloadImgHtml = `<img class="preload-img" src='${preloadSrc}' alt=""/>`;
    }
    // 主 img 元素的 class
    const imgClassList = ['img-dom', `img-fit-${this.props.fit}`];
    if (this.props.lazy) imgClassList.push('lazy');
    if (this.props.animation) imgClassList.push('animation', 'opacity');
    const imgClass = imgClassList.join(' ');

    // 主 img 元素的 src
    const imgSrc = (!this.props.lazy && !this.props.animation) ? this.real_src : PRELOAD_IMG;

    let imgStyle = this.hidden_gif ? 'display: none;' : '';

    // 生成最终的 HTML 字符串
    let divAttrStr = ` class="${divClass}"`
    if (this.props.attrs) {
      divAttrStr += ` ${this.props.attrs}`;
    }
    if (divStyle) {
      divAttrStr += ` style="${divStyle}"`;
    }
    const allowErrorStr = this.props.error ? `data-allow-error="1"` : ''
    const html = `
      <div ${divAttrStr}>
        ${preloadImgHtml}
        <img
          class="${imgClass}"
          alt="image"
          src="${imgSrc}"
          style="${imgStyle}"
          data-original="${this.real_src}"
          ${this.props.imgAttrs}
          ${allowErrorStr}
        />
      </div>
    `;

    return html;
  }

  // 初始化事件监听
  initEvents(rootElement) {
    this.rootElement = rootElement;
    // 获取 img 元素引用
    this.ele = this.rootElement.querySelector('.img-dom');
    if (this.ele) {
      // 处理图片加载
      this.handleImg();
      // 初始化 GIF 相关监听
      if (this.is_gif) {
        this.initGifListener();
      }
    }
  }

  // 销毁组件，解绑事件监听
  destroy() {
    this.releaseGifListener();
  }

  // 处理图片加载（懒加载等）
  handleImg() {
    if (!this.props.lazy && !this.props.animation) {
      return;
    }
    const ele_src = this.ele.getAttribute('src');
    if (this.props.lazy && (!ele_src || ele_src === PRELOAD_IMG)) {
      window.hcImg.observer.observe(this.ele);
    } else {
      setImgSrc(this.ele);
    }
  }

  // 初始化 GIF 监听
  initGifListener() {
    this.releaseGifListener();
    if (['hover', 'selected'].includes(this.props.gif_active_type)) {
      let dom = this.rootElement
      if (this.props.closet_dom_selector) {
        dom = dom.closest(this.props.closet_dom_selector);
      } else if (this.props.hover_dom_handler) {
        dom = this.props.hover_dom_handler();
      }
      this._listeners = [
        {
          dom,
          event: 'mouseenter',
          func: this.changeGifState.bind(this, true),
        },
        {
          dom,
          event: 'mouseleave',
          func: this.changeGifState.bind(this, false),
        },
      ];
    } else if (this.props.gif_active_type === 'focus') {
      this._listeners = [
        {
          dom: window,
          event: 'focus',
          func: this.changeGifState.bind(this, true),
        },
        {
          dom: window,
          event: 'blur',
          func: this.changeGifState.bind(this, false),
        },
      ];
      if (document.hasFocus()) {
        this.changeGifState(true);
      }
    }
    this._listeners.forEach(({ dom, event, func }) => {
      dom.addEventListener(event, func);
    });
  }

  // 释放 GIF 监听
  releaseGifListener() {
    this._listeners.forEach(({ dom, event, func }) => {
      // 元素可能已经被销毁 所以加上 dom 可达性判断 避免报错
      if(dom && typeof dom.removeEventListener === 'function') {
        dom.removeEventListener(event, func);
      }
    });
    this._listeners = [];
  }

  // 改变 GIF 状态
  changeGifState(active) {
    const isSelectedMode = this.props.gif_active_type === 'selected';
    const oldVal = this.is_gif_active
    let newVal
    if (isSelectedMode) {
      newVal = active || this.props.isActive;
    } else {
      newVal = active;
    }
    if(oldVal === newVal) return;
    this.is_gif_active = newVal;
    if (this.ele) {
      this.ele.setAttribute('data-original', this.real_src);
      setImgSrc(this.ele);
    }
  }

  updateImgIsActive(newVal) {
    if (!this.is_gif) return;
    this.props.isActive = newVal;
    this.changeGifState(newVal);
  }
}

export default ChatImg;